import React, { useEffect, useMemo } from "react";
import { ExclamationIcon, StatusOfflineIcon } from "@heroicons/react/outline";
import { Button } from "../Button";
import { classNames } from "../../Utils";
import { useServiceAvailability } from "../../Utils/useServiceAvailability";
import { useTranslation } from "react-i18next";

const MaintenanceAlert = ({
  isVisible = false,
  onClose = () => {},
  onChangeAvailability = () => {},
  onChangeStatus = () => {},
  configValue,
}) => {
  const { t } = useTranslation();

  // Re-evaluated every second, so availability flips by itself at the
  // opening/closing boundary rather than only when the config changes.
  const { config, serviceAvailable, secondsLeft } =
    useServiceAvailability(configValue);

  useEffect(() => {
    onChangeAvailability(serviceAvailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAvailable]);

  useEffect(() => {
    if (config.status) {
      onChangeStatus(config.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.status]);

  const formatTime = (secs) => {
    const hours = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const secsRemaining = secs % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}:${String(secsRemaining).padStart(2, "0")}`;
  };

  const title = useMemo(() => {
    let contentTitle = "serviceMaintenance";

    switch (config.status) {
      case "SCHEDULED_ACTIVE":
      case "OFFLINE":
      case "SCHEDULED_OFFLINE":
        contentTitle = "serviceUnavailable";
        break;
      default:
    }

    return contentTitle;
  }, [config.status]);

  const description = useMemo(() => {
    let contentDescription = t("maintenanceMessage");
    let timeLeftDescription = ` ${t("estimatedCompletionTime")}: `;

    switch (config.status) {
      case "SCHEDULED_ACTIVE":
      case "OFFLINE":
      case "SCHEDULED_OFFLINE":
        contentDescription = t("serviceUnavailableMessage");
        timeLeftDescription = ` ${t("estimatedAvailableTime")}: `;
        break;
      default:
    }

    contentDescription += ` ${t("apologyMessage")}`;

    if (secondsLeft > 0) {
      contentDescription += timeLeftDescription;
    }

    return contentDescription;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.status, secondsLeft]);

  const { iconColor, bgColor } = useMemo(() => {
    let iconColor = "text-yellow-400";

    if (
      config.status === "OFFLINE" ||
      config.status === "SCHEDULED_OFFLINE" ||
      config.status === "SCHEDULED_ACTIVE"
    ) {
      iconColor = "text-gray-400";
    }

    const bgColor = iconColor.replace("text-", "bg-").replace("400", "100");

    return { iconColor, bgColor };
  }, [config.status]);

  if (!isVisible) return null;

  return (
    <div className="mb-6 border border-gray-200 rounded-lg">
      <div className="p-4 bg-white rounded-lg">
        <div className="flex flex-col items-center text-center">
          <div
            className={classNames(
              bgColor,
              "flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full mb-4"
            )}
          >
            {config.status === "OFFLINE" ||
            config.status === "SCHEDULED_OFFLINE" ||
            config.status === "SCHEDULED_ACTIVE" ? (
              <StatusOfflineIcon
                className={`h-5 w-5 ${iconColor}`}
                aria-hidden="true"
              />
            ) : (
              <ExclamationIcon
                className={`h-5 w-5 ${iconColor}`}
                aria-hidden="true"
              />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-md font-medium text-gray-900">{t(title)}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
        </div>

        {secondsLeft > 0 && (
          <div className="mt-3 flex justify-center">
            <span
              className={classNames(
                bgColor,
                "text-md font-semibold p-2 rounded-lg"
              )}
            >
              {formatTime(secondsLeft)}
            </span>
          </div>
        )}

        <div className="mt-4">
          <Button
            type="button"
            className="w-full"
            onClick={onClose}
            title="understand"
            variant="secondary"
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};

export default MaintenanceAlert;
